import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Shield, Users, Building2, ArrowLeft, Save, Trash2, KeyRound, X, Search, Link2, Copy, Package, Ruler } from 'lucide-react';
import ProductsManager from '@/components/ProductsManager';
import UnitsCurrenciesManager from '@/components/UnitsCurrenciesManager';

function UserEditModal({ user, onClose, onSaved, isSuperAdmin }) {
  const [form, setForm] = useState({
    email: user.email || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    company_name: user.company_name || '',
    country: user.country || '',
    lang_code: user.lang_code || 'RU',
    role: user.role || 'participant',
    tender_direction: user.tender_direction || '',
    supplier_direction: user.supplier_direction || '',
    new_password: '',
  });
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm(p => ({ ...p, [key]: e?.target ? e.target.value : e }));

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.new_password) delete payload.new_password;
      if (!payload.tender_direction) payload.tender_direction = null;
      if (payload.role !== 'participant') delete payload.supplier_direction;
      else if (!payload.supplier_direction) payload.supplier_direction = null;
      await api.put(`/admin/users/${user.id}`, payload);
      toast.success('Учётная запись обновлена');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-white border border-slate-200 rounded-sm shadow-2xl w-full max-w-2xl p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="admin-user-edit-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-slate-900">Редактировать учётную запись</h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="admin-modal-close" className="rounded-sm">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Email</Label>
            <Input type="email" value={form.email} onChange={update('email')} data-testid="admin-edit-email" className="border-slate-300 rounded-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Роль</Label>
            <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
              <SelectTrigger data-testid="admin-edit-role" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="organizer">Организатор</SelectItem>
                <SelectItem value="participant">Участник</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Имя</Label>
            <Input value={form.first_name} onChange={update('first_name')} data-testid="admin-edit-first-name" className="border-slate-300 rounded-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Фамилия</Label>
            <Input value={form.last_name} onChange={update('last_name')} data-testid="admin-edit-last-name" className="border-slate-300 rounded-sm" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Компания</Label>
            <Input value={form.company_name} onChange={update('company_name')} data-testid="admin-edit-company" className="border-slate-300 rounded-sm" />
          </div>
          {form.role === 'organizer' && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Направление</Label>
              <Select value={form.tender_direction || 'none'} onValueChange={v => setForm(p => ({ ...p, tender_direction: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="admin-edit-direction" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— не задано —</SelectItem>
                  <SelectItem value="logistics">Логистика</SelectItem>
                  <SelectItem value="purchase">Закупка</SelectItem>
                  <SelectItem value="purchase_oil">Закупка масла</SelectItem>
                  <SelectItem value="all">Все</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {form.role === 'participant' && isSuperAdmin && (
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Направление закупки</Label>
              <Select value={form.supplier_direction || 'none'} onValueChange={v => setForm(p => ({ ...p, supplier_direction: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="admin-edit-supplier-direction" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— унаследовано от организатора —</SelectItem>
                  <SelectItem value="logistics">Логистика</SelectItem>
                  <SelectItem value="purchase">Закупка</SelectItem>
                  <SelectItem value="purchase_oil">Закупка масла</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Страна</Label>
            <Input value={form.country} onChange={update('country')} data-testid="admin-edit-country" className="border-slate-300 rounded-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-slate-700">Язык (для писем)</Label>
            <Select value={form.lang_code} onValueChange={v => setForm(p => ({ ...p, lang_code: v }))}>
              <SelectTrigger data-testid="admin-edit-lang" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="RU">RU</SelectItem>
                <SelectItem value="ENG">ENG</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5" /> Новый пароль <span className="text-[11px] font-normal text-slate-400">(опционально, без подтверждения — от админа)</span>
          </Label>
          <Input type="text" value={form.new_password} onChange={update('new_password')}
            placeholder="оставьте пустым, чтобы не менять" data-testid="admin-edit-password" className="border-slate-300 rounded-sm" />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Отмена</Button>
          <Button onClick={handleSave} disabled={loading} data-testid="admin-edit-save"
            className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
            <Save className="w-4 h-4 mr-1.5" /> {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('organizers');
  const [orgs, setOrgs] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetRequests, setResetRequests] = useState([]);
  const [inviteLink, setInviteLink] = useState(null);  // {link, email, expires_at}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, p, t, r] = await Promise.all([
        api.get('/admin/users?role=organizer'),
        api.get('/admin/users?role=participant'),
        api.get('/tenders'),
        api.get('/admin/password-reset-requests').catch(() => ({ data: [] })),
      ]);
      setOrgs(o.data || []);
      setParticipants(p.data || []);
      setTenders(t.data || []);
      setResetRequests(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  const generateInviteLink = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/invite-link`);
      setInviteLink(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copyInviteLink = async () => {
    if (!inviteLink?.link) return;
    try {
      await navigator.clipboard.writeText(inviteLink.link);
      toast.success('Ссылка скопирована');
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  useEffect(() => { load(); }, [load]);

  if (!user?.is_super_admin) {
    return (
      <div className="p-8 max-w-md mx-auto">
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-sm p-4 text-sm" data-testid="admin-denied">
          Доступ запрещён. Требуются права супер-администратора.
        </div>
      </div>
    );
  }

  const filterBy = (arr, keys) => {
    if (!search) return arr;
    const q = search.toLowerCase();
    return arr.filter(x => keys.some(k => (x[k] || '').toString().toLowerCase().includes(q)));
  };

  const DIR_LABEL = { logistics: 'Логистика', purchase: 'Закупка', purchase_oil: 'Закупка масла', all: 'Все' };

  return (
    <div className="p-3" data-testid="admin-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#0046AD]" /> Администрирование
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Абсолютный доступ: управление всеми учётными записями, лотами и участниками.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} className="rounded-sm">
          <ArrowLeft className="w-4 h-4 mr-1" /> На дашборд
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Поиск по email / компании / имени..." value={search}
          onChange={e => setSearch(e.target.value)} data-testid="admin-search"
          className="pl-9 border-slate-300 rounded-sm" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 mb-4">
          <TabsTrigger value="organizers" data-testid="admin-tab-orgs">
            <Building2 className="w-4 h-4 mr-1.5" /> Организаторы ({orgs.length})
          </TabsTrigger>
          <TabsTrigger value="participants" data-testid="admin-tab-participants">
            <Users className="w-4 h-4 mr-1.5" /> Участники ({participants.length})
          </TabsTrigger>
          <TabsTrigger value="tenders" data-testid="admin-tab-tenders">
            Все лоты ({tenders.length})
          </TabsTrigger>
          <TabsTrigger value="resets" data-testid="admin-tab-resets">
            <KeyRound className="w-4 h-4 mr-1.5" /> Сбросы паролей ({resetRequests.filter(r => !r.resolved).length})
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">
            <Package className="w-4 h-4 mr-1.5" /> Каталог товаров
          </TabsTrigger>
          <TabsTrigger value="units-currencies" data-testid="admin-tab-units-currencies">
            <Ruler className="w-4 h-4 mr-1.5" /> Единицы и валюты
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organizers">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="admin-orgs-list">
            {loading && <div className="p-6 text-sm text-slate-400 text-center">Загрузка...</div>}
            {!loading && filterBy(orgs, ['email','company_name','contact_name','first_name','last_name']).map(u => (
              <div key={u.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50"
                data-testid={`admin-org-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 truncate">{u.contact_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</span>
                    {u.email === 'supply@chemdel.net' && (
                      <Badge variant="outline" className="text-[10px] rounded-sm bg-amber-50 text-amber-700 border-amber-200">SUPER ADMIN</Badge>
                    )}
                    {u.tender_direction && (
                      <Badge variant="outline" className="text-[10px] rounded-sm bg-blue-50 text-blue-700 border-blue-200">
                        {DIR_LABEL[u.tender_direction] || u.tender_direction}
                      </Badge>
                    )}
                    {!u.has_password && (
                      <Badge variant="outline" className="text-[10px] rounded-sm bg-rose-50 text-rose-700 border-rose-200">БЕЗ ПАРОЛЯ</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{u.email} · {u.company_name || '—'} · тендеров: <span className="font-semibold text-slate-700">{u.tenders_count || 0}</span></div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => generateInviteLink(u)}
                    data-testid={`admin-invite-link-org-${u.id}`}
                    className="rounded-sm text-xs" title="Сгенерировать ссылку для установки пароля">
                    <Link2 className="w-3 h-3 mr-1" /> Ссылка
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(u)} data-testid={`admin-edit-org-${u.id}`} className="rounded-sm text-xs">
                    Редактировать
                  </Button>
                  {u.email !== 'supply@chemdel.net' && u.id !== user.id && (
                    <Button size="sm" variant="outline" onClick={async () => {
                      if (!window.confirm(`Удалить учётную запись ${u.email}?`)) return;
                      try {
                        await api.delete(`/admin/users/${u.id}`);
                        toast.success('Удалено');
                        load();
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }} data-testid={`admin-del-org-${u.id}`}
                      className="rounded-sm text-xs text-rose-600 border-rose-200 hover:bg-rose-50">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {!loading && orgs.length === 0 && (
              <div className="p-6 text-sm text-slate-400 text-center">Нет организаторов</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="participants">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="admin-parts-list">
            {loading && <div className="p-6 text-sm text-slate-400 text-center">Загрузка...</div>}
            {!loading && filterBy(participants, ['email','company_name','contact_name','first_name','last_name']).map(u => (
              <div key={u.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50"
                data-testid={`admin-part-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 truncate">{u.contact_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</span>
                    <Badge variant="outline" className="text-[10px] rounded-sm">{u.lang_code || 'RU'}</Badge>
                    {!u.has_password && (
                      <Badge variant="outline" className="text-[10px] rounded-sm bg-rose-50 text-rose-700 border-rose-200">БЕЗ ПАРОЛЯ</Badge>
                    )}
                    {user?.is_super_admin && (
                      <Badge variant="outline"
                        data-testid={`admin-part-direction-${u.id}`}
                        className={`text-[10px] rounded-sm ${
                          u.supplier_direction === 'purchase' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          u.supplier_direction === 'purchase_oil' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          u.supplier_direction === 'logistics' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                          'bg-slate-50 text-slate-500 border-slate-200'
                        }`}>
                        {u.supplier_direction === 'purchase' ? 'Закупка'
                          : u.supplier_direction === 'purchase_oil' ? 'Закупка масла'
                          : u.supplier_direction === 'logistics' ? 'Логистика'
                          : 'без направления'}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{u.email} · {u.company_name || '—'} · ставок: <span className="font-semibold text-slate-700">{u.bids_count || 0}</span></div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => generateInviteLink(u)}
                    data-testid={`admin-invite-link-part-${u.id}`}
                    className="rounded-sm text-xs" title="Сгенерировать ссылку для установки пароля">
                    <Link2 className="w-3 h-3 mr-1" /> Ссылка
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(u)} data-testid={`admin-edit-part-${u.id}`} className="rounded-sm text-xs">
                    Редактировать
                  </Button>
                  <Button size="sm" variant="outline" onClick={async () => {
                    if (!window.confirm(`Удалить ${u.email}?`)) return;
                    try {
                      await api.delete(`/admin/users/${u.id}`);
                      toast.success('Удалено');
                      load();
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }} data-testid={`admin-del-part-${u.id}`}
                    className="rounded-sm text-xs text-rose-600 border-rose-200 hover:bg-rose-50">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
            {!loading && participants.length === 0 && (
              <div className="p-6 text-sm text-slate-400 text-center">Нет участников</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tenders">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="admin-tenders-list">
            {loading && <div className="p-6 text-sm text-slate-400 text-center">Загрузка...</div>}
            {!loading && filterBy(tenders, ['lot_name','product_name','buying_company']).map(t => (
              <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 truncate">{t.lot_name}</span>
                    <Badge variant="outline" className="text-[10px] rounded-sm">{t.phase}</Badge>
                    {t.direction && (
                      <Badge variant="outline" className="text-[10px] rounded-sm bg-blue-50 text-blue-700 border-blue-200">
                        {DIR_LABEL[t.direction] || t.direction}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{t.product_name} · {t.volume} {t.volume_unit || 'т'} · {t.currency} · ставок: {t.bid_count || 0} · приглашено: {t.invited_emails?.length || 0}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate('/dashboard')} className="rounded-sm text-xs">
                  К тендеру
                </Button>
              </div>
            ))}
            {!loading && tenders.length === 0 && (
              <div className="p-6 text-sm text-slate-400 text-center">Нет тендеров</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="resets">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="admin-resets-list">
            {loading && <div className="p-6 text-sm text-slate-400 text-center">Загрузка...</div>}
            {!loading && resetRequests.length === 0 && (
              <div className="p-6 text-sm text-slate-400 text-center">Нет заявок на сброс пароля</div>
            )}
            {!loading && resetRequests.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 truncate">{r.email}</span>
                    {!r.user_exists && (
                      <Badge variant="outline" className="text-[10px] rounded-sm bg-rose-50 text-rose-700 border-rose-200">НЕТ В СИСТЕМЕ</Badge>
                    )}
                    {r.user_exists && (
                      <Badge variant="outline" className="text-[10px] rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">ПИСЬМО ОТПРАВЛЕНО</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">Заявка: {new Date(r.created_at).toLocaleString('ru-RU')}</div>
                </div>
                {r.user_exists && (
                  <Button size="sm" variant="outline"
                    onClick={async () => {
                      const u = [...orgs, ...participants].find(x => x.email === r.email);
                      if (u) await generateInviteLink(u);
                      else toast.error('Пользователь не найден в текущем списке');
                    }}
                    className="rounded-sm text-xs">
                    <Link2 className="w-3 h-3 mr-1" /> Получить ссылку
                  </Button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="products">
          <ProductsManager />
        </TabsContent>

        <TabsContent value="units-currencies">
          <UnitsCurrenciesManager />
        </TabsContent>
      </Tabs>

      {editing && <UserEditModal user={editing} onClose={() => setEditing(null)} onSaved={load} isSuperAdmin={!!user?.is_super_admin} />}

      {/* Модалка со сгенерированной ссылкой */}
      {inviteLink && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
          onClick={() => setInviteLink(null)} data-testid="invite-link-modal">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-[#0046AD]" />
                Ссылка для установки пароля
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setInviteLink(null)} className="rounded-sm">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-xs text-slate-500">
              Для <span className="font-semibold text-slate-700">{inviteLink.email}</span>.
              Действует до {new Date(inviteLink.expires_at).toLocaleString('ru-RU')}.
              Отправьте её пользователю любым удобным способом (если письмо не дошло).
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs font-mono break-all text-slate-700"
              data-testid="invite-link-value">
              {inviteLink.link}
            </div>
            <Button onClick={copyInviteLink} data-testid="invite-link-copy"
              className="w-full bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
              <Copy className="w-4 h-4 mr-2" /> Скопировать ссылку
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
